import './SimulationReportPage.scss';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useParams } from 'react-router-dom';
import { Mesh } from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';

import BackLink from '@components/BackLink';
import Button from '@components/Button';
import { useInjection } from '@components/Injection';
import Layout from '@components/Layout';
import { RoutePath } from '@constants/route-path';
import { IBraceOrder } from '@modulate/shared/src/models/brace-order';
import { IPatient } from '@modulate/shared/src/models/patient';
import { Bounds, OrbitControls } from '@react-three/drei';
import { Canvas, ThreeElements, useFrame, useLoader } from '@react-three/fiber';
import { useAuth } from '@src/contexts/authContext';
import { BRACE_ORDERS_SERVICE, IBraceOrdersService, IPatientsService, PATIENTS_SERVICE } from '@src/services';

interface ModelProps {
  url: string;
  position?: [number, number, number];
  scale?: [number, number, number];
  rotation?: boolean;
  color?: string;
  spin90?: boolean;
  transparent?: boolean;
}

const Model: React.FC<ModelProps> = ({
  url,
  position = [0, 0, 0],
  scale = [1, 1, 1],
  rotation = true,
  color = 'rgb(8, 52, 100)',
  spin90 = true,
  transparent = false,
}) => {
  const meshRef = useRef<Mesh>(null);
  const geometry = useLoader(STLLoader, url);
  useFrame((state, delta) => (meshRef.current.rotation.z += rotation ? delta / 2 : 0));

  useEffect(() => {
    if (meshRef.current) {
      meshRef.current.rotation.set(-Math.PI / 2, 0, spin90 ? Math.PI : -Math.PI / 2);
    }
  }, [geometry]);

  return (
    /* eslint-disable-next-line react/no-unknown-property */
    <mesh ref={meshRef} position={position} scale={scale}>
      {/* eslint-disable-next-line react/no-unknown-property */}
      <primitive object={geometry} attach="geometry" />
      {/* eslint-disable-next-line react/no-unknown-property */}
      <meshStandardMaterial color={color} transparent={transparent} opacity={0.5} />
    </mesh>
  );
};

function SimulationReportPage() {
  const authContext = useAuth();
  const { patientId } = useParams();

  const [patient, setPatient] = useState<IPatient>();
  const [rotation, setRotation] = useState(true);
  const [brace, setBrace] = useState(true);
  const [spine, setSpine] = useState(true);
  const [skin, setSkin] = useState(true);

  const patientsService = useInjection<IPatientsService>(PATIENTS_SERVICE);
  const braceOrdersService = useInjection<IBraceOrdersService>(BRACE_ORDERS_SERVICE);

  useEffect(() => {
    patientsService.getById(patientId, authContext.userObject.clinicId).then(setPatient);
  }, [patientId]);

  const toggleRotation = () => {
    setRotation(!rotation);
  };

  const toggleBrace = () => {
    setBrace(!brace);
  };

  const toggleSpine = () => {
    setSpine(!spine);
  };

  const toggleSkin = () => {
    setSkin(!skin);
  };

  return (
    <Layout>
      <BackLink to={generatePath(RoutePath.SinglePatientPage, { patientId })}>
        {patient ? patient.firstName + ' ' + patient.lastName : ''}
      </BackLink>
      <h1 className="modTitle modTitle--h1">Simulation Report</h1>
      <div className="modReportCanva">
        <div className="modReportToggles">
          <Button onClick={toggleRotation} fullWidth={true}>
            Toggle rotation
          </Button>
          <Button onClick={toggleBrace} fullWidth={true}>
            Toggle brace
          </Button>
          <Button onClick={toggleSpine} fullWidth={true}>
            Toggle spine
          </Button>
          <Button onClick={toggleSkin} fullWidth={true}>
            Toggle skin
          </Button>
        </div>
        <Canvas>
          {/* eslint-disable-next-line react/no-unknown-property */}
          <ambientLight intensity={Math.PI / 2} />
          {/* eslint-disable-next-line react/no-unknown-property */}
          <spotLight position={[100, 100, 100]} angle={0.15} penumbra={1} decay={0} intensity={Math.PI} />
          {/* eslint-disable-next-line react/no-unknown-property */}
          <pointLight position={[-10, -10, -10]} decay={0} intensity={Math.PI} />
          <Bounds fit clip observe margin={1}>
            {spine && (
              <Model
                url="/spine_example.stl"
                position={[0, 0, 0]}
                scale={[10, 10, 10]}
                rotation={rotation}
                color="rgb(242, 235, 227)"
              />
            )}
            {skin && (
              <Model
                url="/skin_example.stl"
                position={[0, 0, 0]}
                scale={[10, 10, 10]}
                rotation={rotation}
                color="rgb(255, 195, 170)"
                transparent={true}
              />
            )}
            {brace && (
              <Model
                url="/brace_example.stl"
                position={[0, 0, 0]}
                scale={[10, 10, 10]}
                rotation={rotation}
                spin90={false}
              />
            )}
          </Bounds>
          <OrbitControls enablePan={false} maxDistance={7500} minDistance={2000} />
        </Canvas>
      </div>
    </Layout>
  );
}

export default SimulationReportPage;
